import { useState } from 'react'
import AskAiModal from './AskAiModal.jsx'

// AskAiBar — sticky bottom action bar that opens the AskAiModal.
//
// Phase 1 of the AI-forward skin redesign. The bar mounts at the
// BOTTOM of each main screen's scrollable column (NOT in App.jsx —
// putting it in the global shell would leak it into modals and the
// print surface).
//
// Crucial rendering choice: the whole thing is a <button>, not an
// <input>. Using an input would:
//   1. Trigger iOS's 16px-or-zoom rule (already handled site-wide in
//      index.css, but a focused input still feels different than what
//      we want — "tap to open chat", not "tap to start typing here").
//   2. Trip the index.css input override and compete for focus styles.
//   3. Encourage the customer to type immediately when there's no
//      submit yet (Phase 1 modal is read-only).
//
// Once the AI chat surface is real (Phase 2+), the bar will become
// the launcher for it; the button shape is the right shape for both
// "open scrolling chat" and "tap to start typing".
//
// Props:
//   placeholder — the text to display in the bar (operator voice,
//                 scope-aware: "Find this month's Sysco invoices…"
//                 on Stock>Receive, "Compare this week to last month…"
//                 on Insights, etc.). Also re-rendered as an eyebrow
//                 inside the modal so the user knows which bar opened.
//   mode        — forwarded to AskAiModal. undefined → coming-soon stub
//                 (every tab except Insights, unchanged). 'insights-query'
//                 → real query mode that calls the ask-restaurant edge fn.
//   restaurantId — multi-unit scope forwarded to the modal (query mode
//                 only). null for single-unit / roll-up.
export default function AskAiBar({ placeholder, mode, restaurantId }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <div className="sticky bottom-0 z-10 mt-6 bg-white px-4 pt-2 safe-bottom">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-[44px] w-full items-center gap-2 rounded-full bg-charcoal-ask py-2 pl-1 pr-1 text-left shadow-md hover:opacity-95 active:opacity-90"
          aria-label="Ask AI"
        >
          {/* Left circle — 22px, dark amber, with a small spark icon.
              Inline SVG so we don't depend on an asset file. */}
          <span
            aria-hidden="true"
            className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-amber-brand-900"
          >
            <SparkIcon />
          </span>

          {/* Center — placeholder copy in the muted tertiary tone. */}
          <span className="flex-1 truncate text-[12px] text-charcoal-text-tertiary">
            {placeholder}
          </span>

          {/* Right circle — 26px, amber-brand-400, white arrow. */}
          <span
            aria-hidden="true"
            className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full bg-amber-brand-400"
          >
            <ArrowIcon />
          </span>
        </button>
      </div>

      <AskAiModal
        open={open}
        onClose={() => setOpen(false)}
        placeholder={placeholder}
        mode={mode}
        restaurantId={restaurantId}
      />
    </>
  )
}

// 14px four-point spark — visually anchors the AI affordance without
// needing an asset. Uses currentColor so the parent tone changes work.
function SparkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-amber-brand-200"
    >
      <path
        d="M7 0.5 L8.4 5.6 L13.5 7 L8.4 8.4 L7 13.5 L5.6 8.4 L0.5 7 L5.6 5.6 Z"
        fill="currentColor"
      />
    </svg>
  )
}

// Right-pointing arrow inside the action chip.
function ArrowIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-amber-brand-900"
    >
      <path
        d="M3 7 L11 7 M7.5 3.5 L11 7 L7.5 10.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
